<template>
  <div>
    <div class="tw-flex tw-items-center tw-gap-4 tw-mb-4">
      <CustomSelect
        :options="entrances"
        label="Подъезд"
        option-label-key="entrance_name"
        option-value-key="project_entrance_id"
        @change="onEntranceChange"
        :value="selectedEntranceId"
        placeholder="Выберите подъезд"
        :clearable="false"
        style="flex: 1;"
      />
      <CustomSelect
        :options="floors"
        label="Этаж"
        option-label-key="floor_name"
        option-value-key="floor_map_id"
        @change="onFloorChange"
        :value="selectedFloorMapId"
        placeholder="Выберите этаж"
        :clearable="false"
        style="flex: 1;"
      />
      <div v-if="selectedFloorMapId" style="padding: 8px 12px; background: #f0f0f0; border-radius: 4px; font-family: monospace; font-size: 11px; color: #555; font-weight: 500;">
        [FM:{{ selectedFloorMapId }}]
        <q-tooltip>floor_map_id</q-tooltip>
      </div>
      <q-btn 
        outline 
        color="primary" 
        label="Развернуть все" 
        @click="expandAll"
        size="sm"
      />
      <q-btn 
        outline 
        color="primary" 
        label="Свернуть все" 
        @click="collapseAll"
        size="sm"
      />
    </div>

    <q-splitter
      v-model="splitterModel"
      style="height: calc(100vh - 200px);"
    >
      <template v-slot:before>
        <!-- Левая колонка с деревом -->
        <div style="height: 100%; overflow-y: auto; padding-right: 20px;">
          <q-linear-progress v-if="this.isFetching" indeterminate />
          <q-tree
            v-else-if="this.treeData.length > 0"
            :nodes="this.treeData"
            node-key="id"
            :expanded.sync="expanded"
            no-nodes-label="Не найдено"
            no-results-label="Не найдено"
            ref="tree"
          >
      <!-- Убираем стандартную иконку раскрытия -->
      <template v-slot:default-control-icon>
        <span></span>
      </template>
      
      <template v-slot:default-header="prop">
        <div 
          class="row items-center" 
          style="width: 100%; gap: 10px; padding: 4px 8px; border-radius: 4px; cursor: pointer;"
          :style="isSelectedGroup(prop.node) ? 'background: #e3f2fd;' : ''"
          @click="handleNodeClick(prop.node)"
        >
            <!-- Иконка галочки для завершенных работ (слева от названия) -->
            <q-icon 
              v-if="prop.node.is_ready_for_work_set === 'full'" 
              name="check_circle" 
              color="green" 
              size="18px"
            >
              <q-tooltip>Работа завершена</q-tooltip>
            </q-icon>
            
            <!-- ID для отладки -->
            <span v-if="prop.node.debug_id" style="font-size: 11px; color: #888; font-family: monospace; margin-right: 8px; font-weight: 500;">
              [{{ prop.node.debug_id }}]
              <q-tooltip v-if="prop.node.debug_id_type">{{ prop.node.debug_id_type }}</q-tooltip>
            </span>
          
          <div
            :class="{'text-weight-bold': prop.node.isBold}"
            style="color: #000; flex: 1;"
          >
            {{ prop.node.label }}
          </div>
          
          <!-- Иконка замечаний -->
          <div v-if="prop.node.is_defect_exist" style="display: flex; align-items: center; gap: 5px;">
            <q-icon name="warning" color="red" size="18px">
              <q-tooltip>Есть замечания</q-tooltip>
            </q-icon>
          </div>

          <!-- Статус ОКК для группы работ -->
          <div v-if="prop.node.checked_status" 
               style="padding: 6px 12px; border-radius: 4px; font-size: 12px; font-weight: 500; white-space: nowrap;"
               :style="`background: ${prop.node.checked_status_colour || '#90caf9'}; color: #000;`">
            {{ prop.node.checked_status }}
          </div>
          
          <!-- Статус частичного выполнения работ -->
          <div v-if="prop.node.is_ready_for_work_set === 'half'" 
               style="padding: 6px 12px; border-radius: 4px; font-size: 12px; font-weight: 500; background: #fbbf24; color: #000; white-space: nowrap;">
            Готов к сдаче работ. Умирзаков Дихан, {{ prop.node.done }}/{{ prop.node.num }}
          </div>

          <div
            v-if="prop.node.total_sum !== undefined"
            :class="{'text-weight-bold': prop.node.isBold}"
            style="color: #000; margin-left: auto;"
          >
            <Amount :value="Math.round(prop.node.total_sum)" /> тг
          </div>
        </div>
      </template>
          </q-tree>
          <div v-else style="text-align: center; padding: 20px; color: #666;">
            Нет данных для отображения
          </div>
        </div>
      </template>

      <template v-slot:after>
        <!-- Правая колонка для табличной информации -->
        <div style="height: 100%; overflow-y: auto; padding-left: 20px;">
              <div v-if="!selectedGroupData" style="padding: 20px; background: #f5f5f5; border-radius: 8px; min-height: 200px;">
                <h3 style="margin: 0 0 16px 0; font-size: 16px; font-weight: 600;">Детальная информация</h3>
                <p style="color: #666; margin: 0;">Выберите группу работ в дереве для просмотра подробной информации</p>
              </div>
        
        <div v-else-if="isLoadingBreakdown" style="padding: 20px;">
          <q-linear-progress indeterminate />
        </div>
        
        <div v-else-if="breakdownData && breakdownData.length > 0">
          <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 16px;">
            <div style="display: flex; gap: 16px; align-items: center;">
              <q-toggle 
                v-model="showDetailedColumns" 
                label="Детальные колонки"
                dense
              />
              <q-toggle 
                v-model="groupByRoomAndWork" 
                label="Группировка"
                dense
              />
              <q-toggle 
                v-model="showMaterials" 
                label="Показывать материалы"
                dense
              />
            </div>
          </div>
          
          <q-table
            :rows="processedBreakdownData"
            :columns="breakdownColumns"
            row-key="_id"
            flat
            bordered
            dense
            :pagination="{rowsPerPage: 0}"
            hide-pagination
          >
            <template v-slot:body="props">
              <q-tr 
                :props="props"
                @click="openHistoryDialog(props.row)"
                style="cursor: pointer;"
                class="hover-bg-grey-1"
              >
                <q-td :props="props" key="room_name" style="max-width: 150px;">
                  <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ props.row.room_name }}
                    <q-tooltip>{{ props.row.room_name }}</q-tooltip>
                  </div>
                </q-td>
                <q-td :props="props" key="work_set_name" style="max-width: 200px;">
                  <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ props.row.work_set_name }}
                    <q-tooltip>{{ props.row.work_set_name }}</q-tooltip>
                  </div>
                  <!-- Объем и Цена под работой, если включены детальные колонки -->
                  <div v-if="showDetailedColumns" style="font-size: 11px; color: #666; margin-top: 4px;">
                    <span style="margin-right: 12px;">Объем: {{ Number(props.row.work_set_amount || 0).toFixed(3) }}</span>
                    <span>Цена: {{ Math.round(props.row.work_set_price || 0) }} тг</span>
                  </div>
                </q-td>
                
                <!-- Объединенное поле ID / Смета -->
                <q-td 
                  v-if="!groupByRoomAndWork" 
                  :props="props" 
                  key="remont_smeta"
                >
                  <div style="font-size: 11px;">
                    <div style="font-weight: 500; margin-bottom: 4px;">
                      ID: {{ props.row.remont_id }}
                      <q-tooltip>remont_id</q-tooltip>
                    </div>
                    <div style="color: #1976d2; font-weight: 500;">
                      <q-icon name="history" size="14px" style="margin-right: 4px;" />
                      v{{ props.row.smeta_version_no }}
                    </div>
                    <div style="color: #666; margin-top: 2px;">{{ props.row.smeta_reason_name }}</div>
                    <div v-if="props.row.smeta_comments" style="color: #999; margin-top: 2px; font-style: italic;">
                      {{ props.row.smeta_comments }}
                    </div>
                  </div>
                </q-td>
                
                <!-- Сумма в конце -->
                <q-td :props="props" key="total_sum" class="text-right">
                  <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;">
                    <Amount :value="Math.round(props.row.total_sum || 0)" /> тг
                    
                    <!-- Иконка для is_checked -->
                    <q-icon 
                      v-if="props.row.is_checked === true" 
                      name="check_circle" 
                      color="green" 
                      size="20px"
                    >
                      <q-tooltip>Работа сдана</q-tooltip>
                    </q-icon>
                    
                    <q-icon 
                      v-else-if="props.row.is_checked === 'mixed'" 
                      name="warning" 
                      color="orange" 
                      size="20px"
                    >
                      <q-tooltip>Аномалия: есть сданные и несданные работы</q-tooltip>
                    </q-icon>
                  </div>
                </q-td>
              </q-tr>
              
              <!-- Строка с материалами под работой -->
              <q-tr v-if="showMaterials && props.row.materials && props.row.materials.length > 0">
                <q-td :colspan="breakdownColumns.length" style="background: #fafafa; padding: 8px 16px;">
                  <div style="font-weight: 500; margin-bottom: 8px; color: #666; font-size: 12px;">Материалы:</div>
                  <div v-for="(mat, idx) in props.row.materials" :key="idx" style="font-size: 11px; margin-bottom: 6px; padding: 4px 8px; background: white; border-left: 3px solid #1976d2; border-radius: 2px;">
                    <div style="font-weight: 500; margin-bottom: 2px;">{{ mat.material_name }}</div>
                    <div style="color: #666; font-size: 10px;">
                      Количество: <strong>{{ mat.material_amount }}</strong> | 
                      Цена: <strong>{{ Math.round(mat.material_price || 0) }} тг</strong> | 
                      Сумма: <strong>{{ Math.round(mat.material_total || 0) }} тг</strong>
                      <span v-if="mat.purchase_type_name" style="color: #999;"> ({{ mat.purchase_type_name }})</span>
                    </div>
                  </div>
                </q-td>
              </q-tr>
            </template>
            
            <template v-slot:bottom-row>
              <q-tr style="background: #f5f5f5; font-weight: 600;">
                <q-td :colspan="breakdownColumns.length - 1" style="text-align: right; padding: 12px;">
                  ИТОГО:
                </q-td>
                <q-td style="text-align: right; padding: 12px;">
                  <Amount :value="Math.round(breakdownTotalSum)" /> тг
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </div>
        
        <div v-else style="padding: 20px; background: #f5f5f5; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Нет данных для отображения</p>
        </div>
        </div>
      </template>
    </q-splitter>
    
    <!-- Модальное окно с историей сметы -->
    <q-dialog v-model="showHistoryDialog">
      <q-card style="min-width: 800px; max-width: 1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">История сметы</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section v-if="selectedRowForHistory">
          <div style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 8px;">
            <div style="font-weight: 500; margin-bottom: 4px;">{{ selectedRowForHistory.room_name }} - {{ selectedRowForHistory.work_set_name }}</div>
            <div style="font-size: 12px; color: #666;">ID ремонта: {{ selectedRowForHistory.remont_id }}</div>
          </div>
          
          <div v-if="selectedRowForHistory.smeta_history && selectedRowForHistory.smeta_history.length > 0">
            <q-timeline color="primary">
              <q-timeline-entry
                v-for="(version, idx) in selectedRowForHistory.smeta_history"
                :key="idx"
                :title="`Версия ${version.version_no}`"
                :subtitle="version.version_date"
                :icon="idx === 0 ? 'star' : 'update'"
              >
                <div style="background: white; padding: 16px; border-radius: 8px; margin-top: 8px; border: 1px solid #e0e0e0;">
                  <div style="margin-bottom: 12px;">
                    <strong>Причина:</strong> {{ version.reason_name }}
                  </div>
                  <div v-if="version.comments" style="margin-bottom: 12px;">
                    <strong>Комментарий:</strong> {{ version.comments }}
                  </div>
                  <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 12px; padding: 12px; background: #f9f9f9; border-radius: 4px;">
                    <div>
                      <div style="color: #666; font-size: 12px; margin-bottom: 4px;">Объем:</div>
                      <div style="font-weight: 600; font-size: 16px;">{{ Number(version.work_set_amount || 0).toFixed(3) }}</div>
                    </div>
                    <div>
                      <div style="color: #666; font-size: 12px; margin-bottom: 4px;">Цена:</div>
                      <div style="font-weight: 600; font-size: 16px;">{{ Math.round(version.work_set_price || 0) }} тг</div>
                    </div>
                    <div>
                      <div style="color: #666; font-size: 12px; margin-bottom: 4px;">Сумма:</div>
                      <div style="font-weight: 600; font-size: 16px;">{{ Math.round(version.total_sum || 0) }} тг</div>
                    </div>
                  </div>
                  
                  <div v-if="version.materials && version.materials.length > 0" style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e0e0e0;">
                    <div style="font-weight: 600; margin-bottom: 12px; font-size: 14px;">Материалы:</div>
                    <div style="display: grid; gap: 8px;">
                      <div v-for="(mat, midx) in version.materials" :key="midx" style="padding: 8px 12px; background: #f5f5f5; border-radius: 4px; border-left: 3px solid #1976d2;">
                        <div style="font-weight: 500; margin-bottom: 4px;">{{ mat.material_name }}</div>
                        <div style="font-size: 12px; color: #666;">
                          Количество: {{ mat.material_amount }} | 
                          Цена: {{ Math.round(mat.material_price || 0) }} тг | 
                          Сумма: {{ Math.round(mat.material_total || 0) }} тг
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </q-timeline-entry>
            </q-timeline>
          </div>
          
          <div v-else style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;">
            <q-icon name="info" size="48px" color="grey-5" />
            <p style="color: #666; margin: 16px 0 0 0;">История сметы отсутствует</p>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { getFloorWorkSetsManager, getEntranceApartments, getFloorWorkSetBreakdown } from '@/views/ResidentialSettings/services';
import { getEntrancesByProject } from '@/views/ProjectsEntry/services';
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';

export default {
  name: "ProjectSmetaManager",
  components: { Amount, CustomSelect },
  props: {
    projectId: {
      type: Number,
      required: true
    },
    residentId: {
      type: Number,
      default: null
    },
    projectTypeId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      isFetching: false,
      data: null,
      treeData: [],
      entrances: [],
      floors: [],
      selectedEntranceId: null,
      selectedFloorMapId: null,
      expanded: [],
      selectedGroupData: null,
      breakdownData: [],
      isLoadingBreakdown: false,
      showDetailedColumns: false,
      groupByRoomAndWork: false,
      showMaterials: false,
      splitterModel: 50,
      selectedRowForHistory: null,
      showHistoryDialog: false
    }
  },
  computed: {
    breakdownColumns() {
      const columns = [];
      
      columns.push(
        { name: 'room_name', label: 'Помещение', field: 'room_name', align: 'left', sortable: true, style: 'max-width: 150px;' },
        { name: 'work_set_name', label: 'Работа', field: 'work_set_name', align: 'left', sortable: true, style: 'max-width: 200px;' }
      );
      
      // remont_id и смета объединены в одно поле (показываем только без группировки)
      if (!this.groupByRoomAndWork) {
        columns.push({ name: 'remont_smeta', label: 'ID / Смета', field: 'remont_id', align: 'left', sortable: true });
      }
      
      // Сумма в конце
      columns.push({ name: 'total_sum', label: 'Сумма', field: 'total_sum', align: 'right', sortable: true });
      
      return columns;
    },
    
    processedBreakdownData() {
      if (!this.breakdownData || this.breakdownData.length === 0) return [];
      
      if (this.groupByRoomAndWork) {
        // Группировка по комнате и работе
        const grouped = {};
        
        this.breakdownData.forEach(row => {
          const key = `${row.room_name}_${row.work_set_name}`;
          
          if (!grouped[key]) {
            grouped[key] = {
              room_name: row.room_name,
              work_set_name: row.work_set_name,
              work_set_amount: 0,
              work_set_price: 0,
              total_sum: 0,
              materials: [],
              remont_ids: [],
              is_checked_values: []
            };
          }
          
          grouped[key].work_set_amount += (row.work_set_amount || 0);
          grouped[key].total_sum += (row.total_sum || 0);
          grouped[key].remont_ids.push(row.remont_id);
          grouped[key].is_checked_values.push(row.is_checked);
          
          // Группируем материалы
          if (row.materials && row.materials.length > 0) {
            row.materials.forEach(mat => {
              const existingMat = grouped[key].materials.find(m => m.material_name === mat.material_name);
              if (existingMat) {
                existingMat.material_amount += (mat.material_amount || 0);
                existingMat.material_total += (mat.material_total || 0);
              } else {
                grouped[key].materials.push({
                  material_name: mat.material_name,
                  material_amount: mat.material_amount || 0,
                  material_price: mat.material_price || 0,
                  material_total: mat.material_total || 0,
                  purchase_type_name: mat.purchase_type_name
                });
              }
            });
          }
        });
        
        return Object.values(grouped).map((item, idx) => {
          // Определяем статус is_checked для группы
          const uniqueCheckedValues = [...new Set(item.is_checked_values)];
          let is_checked_status = null;
          
          if (uniqueCheckedValues.length === 1) {
            // Все одинаковые
            is_checked_status = uniqueCheckedValues[0];
          } else {
            // Есть разные значения - аномалия
            is_checked_status = 'mixed';
          }
          
          return {
            ...item,
            _id: `grouped_${idx}`,
            remont_id: item.remont_ids.join(', '),
            is_checked: is_checked_status
          };
        });
      } else {
        // Без группировки - возвращаем как есть
        return this.breakdownData.map((item, idx) => ({
          ...item,
          _id: item.remont_work_set_id || idx
        }));
      }
    },
    
    breakdownTotalSum() {
      if (!this.processedBreakdownData || this.processedBreakdownData.length === 0) return 0;
      return this.processedBreakdownData.reduce((sum, row) => sum + (row.total_sum || 0), 0);
    }
  },
  methods: {
    expandAll() {
      const allNodeIds = [];
      const collectIds = (nodes) => {
        nodes.forEach(node => {
          allNodeIds.push(node.id);
          if (node.children && node.children.length > 0) {
            collectIds(node.children);
          }
        });
      };
      collectIds(this.treeData);
      this.expanded = allNodeIds;
    },
    collapseAll() {
      this.expanded = [];
    },
    toggleNode(node) {
      const index = this.expanded.indexOf(node.id);
      if (index > -1) {
        // Узел раскрыт, скрываем его
        this.expanded.splice(index, 1);
      } else {
        // Узел скрыт, раскрываем его
        this.expanded.push(node.id);
      }
    },
    isSelectedGroup(node) {
      // Проверяем, является ли этот узел выбранной группой работ
      if (!this.selectedGroupData || !node.work_set_check_group_id) {
        return false;
      }
      return node.work_set_check_group_id === this.selectedGroupData.work_set_check_group_id &&
             node.placement_type_id === this.selectedGroupData.placement_type_id;
    },
    async loadEntrances() {
      const res = await getEntrancesByProject(this.projectId);
      if (res && res.length > 0) {
        this.entrances = res;
        this.selectedEntranceId = res[0].project_entrance_id;
        await this.loadFloors();
      }
    },
    async loadFloors() {
      if (!this.selectedEntranceId) return;
      
      const params = {
        resident_id: this.residentId,
        project_type_id: this.projectTypeId,
        project_entrance_id: this.selectedEntranceId
      };
      
      const res = await getEntranceApartments(params);
      if (res && res.data && res.data.length > 0) {
        this.floors = res.data.map(floor => ({
          ...floor,
          floor_name: `Этаж ${floor.floor}`
        }));
        this.selectedFloorMapId = this.floors[0].floor_map_id;
        await this.getData();
      }
    },
    async onEntranceChange(value) {
      this.selectedEntranceId = value;
      this.selectedFloorMapId = null;
      this.floors = [];
      this.treeData = [];
      await this.loadFloors();
    },
    async onFloorChange(value) {
      this.selectedFloorMapId = value;
      this.selectedGroupData = null;
      this.breakdownData = [];
      await this.getData();
    },
    handleNodeClick(node) {
      console.log('Node clicked:', node);
      
      // Сначала раскрываем/скрываем узел, если у него есть дети
      if (node.children && node.children.length > 0) {
        this.toggleNode(node);
      }
      
      // Затем, если это группа работ, загружаем данные
      if (node.work_set_check_group_id && node.placement_type_id) {
        console.log('Loading breakdown for group:', node.work_set_check_group_id);
        
        this.selectedGroupData = {
          placement_type_id: node.placement_type_id,
          work_set_check_group_id: node.work_set_check_group_id
        };
        this.selectedRowForHistory = null; // Сбрасываем выбранную строку при смене группы
        this.loadBreakdown();
      }
    },
    openHistoryDialog(row) {
      console.log('Opening history for row:', row);
      this.selectedRowForHistory = row;
      this.showHistoryDialog = true;
    },
    async loadBreakdown() {
      if (!this.selectedGroupData || !this.selectedFloorMapId) return;
      
      this.isLoadingBreakdown = true;
      const res = await getFloorWorkSetBreakdown(
        this.selectedFloorMapId,
        this.projectId,
        this.selectedGroupData.placement_type_id,
        this.selectedGroupData.work_set_check_group_id
      );
      this.isLoadingBreakdown = false;
      
      if (res && res.data) {
        this.breakdownData = res.data;
      } else {
        this.breakdownData = [];
      }
    },
    async getData() {
      if (!this.selectedFloorMapId) return;
      
      this.isFetching = true;
      const res = await getFloorWorkSetsManager(this.selectedFloorMapId);
      this.isFetching = false;
      if (!res) return;
      this.data = res;
      this.buildTreeData();
    },
    buildTreeData() {
      if (!this.data?.data) {
        this.treeData = [];
        return;
      }

      this.treeData = this.data.data.map((placement, pIndex) => {
        const placementNode = {
          id: `placement_${placement.placement_type_id}`,
          label: `${placement.placement_type_name} (${placement.placement_percent || 0}%)`,
          total_sum: placement.total_sum || 0,
          isBold: true,
          color: placement.is_defect_exist ? '#ff0000' : '#000',
          is_defect_exist: placement.is_defect_exist,
          placement_percent: placement.placement_percent,
          placement_okk_status_colours: placement.placement_okk_status_colours,
          debug_id: `PT:${placement.placement_type_id}`,
          debug_id_type: 'placement_type_id',
          children: []
        };

        if (placement.work_set_check_groups && placement.work_set_check_groups.length > 0) {
          placementNode.children = placement.work_set_check_groups.map((group, gIndex) => {
            const groupNode = {
              id: `group_${placement.placement_type_id}_${group.work_set_check_group_id}`,
              label: group.work_set_check_group_name,
              total_sum: group.total_sum || 0,
              isBold: false,
              color: group.checked_status_colour || '#000',
              is_defect_exist: group.is_defect_exist,
              checked_status: group.checked_status,
              checked_status_code: group.checked_status_code,
              checked_status_colour: group.checked_status_colour,
              to_disable: group.to_disable,
              debug_id: `WG:${group.work_set_check_group_id}`,
              debug_id_type: 'work_set_check_group_id',
              placement_type_id: placement.placement_type_id,
              work_set_check_group_id: group.work_set_check_group_id,
              children: []
            };

            if (group.work_sets && group.work_sets.length > 0) {
              groupNode.children = group.work_sets.map((workSet, wIndex) => {
                const done = workSet.done || 0;
                const num = workSet.num || 0;
                
                return {
                  id: `workset_${placement.placement_type_id}_${group.work_set_check_group_id}_${workSet.work_set_id}`,
                  label: `${workSet.work_set_name}`,
                  total_sum: workSet.total_sum || 0,
                  isBold: false,
                  color: '#404040',
                  is_ready_for_work_set: workSet.is_ready_for_work_set,
                  done: done,
                  num: num,
                  unit_name: workSet.unit_name,
                  work_set_amount: workSet.work_set_amount,
                  to_disable: workSet.to_disable,
                  debug_id: `WS:${workSet.work_set_id}`,
                  debug_id_type: 'work_set_id'
                };
              });
            }

            return groupNode;
          });
        }

        return placementNode;
      });
      
      // Автоматически развернуть все узлы после построения дерева
      this.$nextTick(() => {
        this.expandAll();
      });
    }
  },
  mounted() {
    this.loadEntrances();
  }
};
</script>

<style scoped lang="scss">
</style>

